import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
    type ChartData,
    type ChartDataset,
} from "chart.js";
import * as React from "react";
import { Bar } from "react-chartjs-2";
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

import ExpandLess from "@mui/icons-material/UnfoldLess";
import ExpandMoreIcon from "@mui/icons-material/UnfoldMore";
import { Card, LinearProgress } from "@mui/material";
import "./styles.css";

export interface IBarStyle {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    hoverBackgroundColor?: string;
    hoverBorderColor?: string;
}

interface Props {
    title: string;
    data?: ChartData<"bar", number[], string>;
    onClick?: () => any;
    onElementsClick?: (e: IElementClick[]) => any;
    isSelected?: boolean;
    height?: number;
}

export interface IElementClick {
    data?: any;
    dsIndex: number;
    dsLabel: string;
    label: string;
    index: number;
}

export const colors = [
    "33, 150, 243",
    "150, 150, 243",
    "150, 33, 243",
    "243, 33, 150",
    "243, 150, 150",
    "243, 150, 33",
    "33, 243, 150",
    "150, 243, 150",
    "150, 243, 33",
    "150, 243, 33",
    "33, 243, 33",
    "33, 243, 150",
];

export function initDataSet(label: string, style: IBarStyle): ChartDataset<"bar", number[]> {
    const ds: ChartDataset<"bar", number[]> = {
        ...style,
        label,
        type: "bar",
        borderWidth: 1,
        data: [],
    };

    return ds;
}

export function getStyle(rgbColor: string): IBarStyle {
    const style: IBarStyle = {
        backgroundColor: `rgba(${rgbColor}, .2)`,
        borderColor: `rgba(${rgbColor}, 1)`,
        hoverBackgroundColor: `rgba(${rgbColor}, .5)`,
        hoverBorderColor: `rgba(${rgbColor}, 1)`,
    };

    return style;
}

type IChartElement = { datasetIndex: number; index: number };

export class BarChart extends React.Component<Props> {
    onClick = () => {
        const { onClick } = this.props;
        if (onClick) onClick();
    };

    onElementsClick = (e: IChartElement[]) => {
        if (!e || e.length < 1 || !this.props.onElementsClick) return;
        const { data } = this.props;
        if (!data || !data.datasets || !data.labels) return;

        const labels = data.labels as string[];
        const datasets = data.datasets as ChartDataset<"bar", number[]>[];

        const hits = e
            .map((hit) => {
                const dataset = datasets[hit.datasetIndex];
                if (!dataset || !dataset.data || !dataset.label || !labels) return undefined;
                const index = hit.index;
                return {
                    data: dataset.data[index],
                    dsIndex: hit.datasetIndex,
                    dsLabel: dataset.label as string,
                    label: labels[index] as string,
                    index,
                };
            })
            .filter((x) => x) as IElementClick[];

        this.props.onElementsClick(hits);
    };

    render() {
        const { isSelected, data, title, onClick, height } = this.props;

        if (!data) {
            return (
                <Card className="DashboardBarChart">
                    {title}
                    <LinearProgress style={{ width: "100%" }} />
                </Card>
            );
        }

        return (
            <Card className="DashboardBarChart" raised={isSelected}>
                <div className="DashboardBarChart_title" onClick={this.onClick}>
                    <span>{title}</span>
                    {onClick && (isSelected ? <ExpandLess /> : <ExpandMoreIcon />)}
                </div>
                <div className="DashboardBarChart_graph">
                    <Bar
                        data={data}
                        width={100}
                        height={height || 250}
                        // react-chartjs-2 v5: use Chart.js onClick in options to get active elements
                        options={{
                            maintainAspectRatio: false,
                            scales: {
                                x: { stacked: true },
                                y: { stacked: true },
                            },
                            interaction: { mode: "index", intersect: false },
                            plugins: {
                                legend: { display: true },
                                tooltip: {},
                            },
                            onClick: (_event, elements) => {
                                const normalized = elements?.map((el: any) => ({ datasetIndex: el.datasetIndex, index: el.index })) ?? [];
                                this.onElementsClick(normalized);
                            },
                        }}
                    />
                </div>
            </Card>
        );
    }
}
